import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { SESSION_QUERY_KEY } from '@/lib/api/session';
import { syncSession, terminateSession } from '@/lib/api/auth';
import { resolveTenantDashboardPath } from '@/lib/tenantNavigation';
import { useSession } from '@/lib/hooks/useSession';
import { invalidateCsrfToken } from '@/lib/security/csrf';
import { useTenantStore } from '@/stores/tenantStore';
import { syncSupabaseSessionCookies } from '@/integrations/supabase/sessionCookies';
import { loadOnboardingSnapshot } from '@/features/onboarding/storage';

export interface UseAuthResult {
  user: User | null;
  supabaseSession: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  sessionQuery: ReturnType<typeof useSession>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<UseAuthResult | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const isDemoMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return new URLSearchParams(window.location.search).get('mode') === 'demo';
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const sessionQuery = useSession();
  const triggerSessionRefetch = sessionQuery.triggerRefetch;
  const sessionState = sessionQuery.data.state;
  const [supabaseSession, setSupabaseSession] = useState<Session | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const queryClient = useQueryClient();
  const activeSignOutTokenRef = useRef<symbol | null>(null);
  const activeSyncPromiseRef = useRef<Promise<void> | null>(null);
  const lastTenantRepairAtRef = useRef(0);
  const clearTenant = useTenantStore((state) => state.clearTenant);
  const supabaseUserId = supabaseSession?.user?.id ?? null;

  const synchronizeSession = useCallback(
    (reason: string) => {
      if (activeSyncPromiseRef.current) {
        return activeSyncPromiseRef.current;
      }

      const task = (async () => {
        setSyncing(true);
        try {
          await syncSession();
          await triggerSessionRefetch().catch(() => undefined);
        } catch (error) {
          if (import.meta.env.DEV) {
            console.warn(`[useAuth] syncSession failed (${reason}):`, error);
          }
          throw error;
        } finally {
          activeSyncPromiseRef.current = null;
          setSyncing(false);
        }
      })();

      activeSyncPromiseRef.current = task;
      return task;
    },
    [triggerSessionRefetch],
  );

  const handleSessionHydration = useCallback(
    async (session: Session | null) => {
      if (session) {
        activeSignOutTokenRef.current = null;
      }

      setSupabaseSession(session);
      setSupabaseUser(session?.user ?? null);
      syncSupabaseSessionCookies(session);

      if (!session) {
        await triggerSessionRefetch().catch(() => undefined);
        return;
      }

      await synchronizeSession('supabase-hydration').catch(() => undefined);
    },
    [triggerSessionRefetch, synchronizeSession],
  );

  useEffect(() => {
    let isMounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!isMounted) {
          return;
        }
        setSupabaseSession(data.session ?? null);
        setSupabaseUser(data.session?.user ?? null);
        syncSupabaseSessionCookies(data.session ?? null);
        setInitializing(false);
      })
      .catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('[useAuth] getSession failed:', error);
        }
        setInitializing(false);
      });

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) {
        return;
      }
      await handleSessionHydration(session);
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [handleSessionHydration]);

  useEffect(() => {
    if (initializing || !supabaseUserId) {
      return;
    }

    if (sessionState !== 'TENANT_UNASSIGNED') {
      return;
    }

    const now = Date.now();
    const TENANT_REPAIR_MIN_INTERVAL_MS = 5_000;
    if (now - lastTenantRepairAtRef.current < TENANT_REPAIR_MIN_INTERVAL_MS) {
      return;
    }
    lastTenantRepairAtRef.current = now;

    synchronizeSession('tenant-repair').catch(() => undefined);
  }, [initializing, sessionState, supabaseUserId, synchronizeSession]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (isDemoMode()) {
      return;
    }
    if (activeSignOutTokenRef.current) {
      // Allow the in-flight sign-out to control navigation (e.g., redirect to home)
      // so onboarding guard logic doesn't override the intended destination.
      return;
    }

    const sessionData = sessionQuery.data;
    if (!sessionData.authenticated || sessionData.state !== 'OK' || !sessionData.tenant?.slug) {
      return;
    }

    const slug = sessionData.tenant.slug;
    const path = window.location.pathname;
    const slugPrefix = `/r/${slug}`;
    const onboardingStatus = sessionData.onboardingStatus ?? 'completed';

    if (onboardingStatus === 'pending') {
      const onboardingSnapshot = loadOnboardingSnapshot();
      if (!onboardingSnapshot) {
        return;
      }
      const targetPath = onboardingSnapshot?.paymentComplete
        ? '/onboarding/questionnaire'
        : '/onboarding/payment';

      if (path !== targetPath) {
        window.location.replace(targetPath);
      }
      return;
    }

    const isAuthRoute = path.startsWith('/auth');
    const isHome = path === '/' || path === '';

    if (!isAuthRoute && !isHome) {
      return;
    }

    const dashboardPath = resolveTenantDashboardPath(`${slugPrefix}/dashboard`);
    if (dashboardPath !== path) {
      window.location.replace(dashboardPath);
    }
  }, [sessionQuery.data]);

  const signOut = useCallback(async () => {
    const failures: Array<{ stage: string; error: unknown }> = [];
    const token = Symbol('sign-out');
    activeSignOutTokenRef.current = token;
    const isActive = () => activeSignOutTokenRef.current === token;

    const runStage = async (stage: string, runner: () => Promise<void>) => {
      if (!isActive()) {
        return;
      }
      try {
        await runner();
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn(`[useAuth] ${stage} failed during signOut:`, error);
        }
        failures.push({ stage, error });
      }
    };

    await runStage('api', () => terminateSession());
    await runStage('auth', () => supabase.auth.signOut());
    syncSupabaseSessionCookies(null);

    if (isActive()) {
      invalidateCsrfToken();
      clearTenant();
    }

    if (isActive() && typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem('braise-tenant-store');
      } catch (storageError) {
        if (import.meta.env.DEV) {
          console.warn('[useAuth] failed to clear tenant storage:', storageError);
        }
      }
    }

    await runStage('queries', () => queryClient.cancelQueries({ type: 'all' }));

    if (isActive()) {
      queryClient.removeQueries({ type: 'all' });
      queryClient.setQueryData(SESSION_QUERY_KEY, {
        authenticated: false,
        user: null,
        tenant: null,
        state: 'UNAUTHENTICATED',
        permissions: [],
      });
    }

    if (isActive()) {
      await sessionQuery.triggerRefetch().catch(() => undefined);
    }

    if (failures.length && isActive()) {
      const message = failures
        .map((entry) => `${entry.stage}: ${entry.error instanceof Error ? entry.error.message : String(entry.error)}`)
        .join('; ');
      console.error('[useAuth] signOut completed with recoverable errors:', message, failures);
    }

    if (activeSignOutTokenRef.current === token) {
      activeSignOutTokenRef.current = null;
    }
  }, [clearTenant, queryClient, sessionQuery]);

  const loading = useMemo(
    () => initializing || syncing || sessionQuery.isLoading,
    [initializing, syncing, sessionQuery.isLoading],
  );

  const value = useMemo<UseAuthResult>(
    () => ({
      user: supabaseUser,
      supabaseSession,
      loading,
      signOut,
      sessionQuery,
    }),
    [loading, signOut, sessionQuery, supabaseSession, supabaseUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): UseAuthResult => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
